import { Button, Divider, Flex, Spacing, Text } from '@billy/design-system'
import { Fragment, ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ImportedInvoiceReviewFormSchema } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'

import { useFieldArray, useFormContext } from '../../../../../../../../hooks/useForm'
import { getDefaultImportedInvoiceReviewLine } from '../../utils/getDefaultImportedInvoiceReviewLine'
import { ImportedInvoiceReviewLine } from './elements/ImportedInvoiceReviewLine'

interface ImportedInvoiceReviewLinesProps {
  isReadOnly: boolean
}

export const ImportedInvoiceReviewLines = ({ isReadOnly }: ImportedInvoiceReviewLinesProps): ReactElement => {
  const { t } = useTranslation()
  const { control, formState } = useFormContext<ImportedInvoiceReviewFormSchema>()

  const { append: addLine, fields: lines, remove: removeLine } = useFieldArray({ control, name: 'lines' })

  const isDisabled = isReadOnly || formState.isSubmitting

  // A single line needs no name to tell it apart, which is the difference between the two frames.
  const hasSeveralLines = lines.length > 1

  const handleAddLineClick = useCallback(() => {
    addLine(getDefaultImportedInvoiceReviewLine())
  }, [addLine])

  const handleLineRemove = useCallback(
    (lineIndex: number) => {
      removeLine(lineIndex)
    },
    [removeLine],
  )

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      {lines.map((line, lineIndex) => (
        <Fragment key={line.id}>
          {lineIndex > 0 && <Divider />}
          <ImportedInvoiceReviewLine
            isReadOnly={isReadOnly}
            lineIndex={lineIndex}
            onRemove={handleLineRemove}
            withName={hasSeveralLines}
          />
        </Fragment>
      ))}

      {!isReadOnly && (
        <>
          <Divider />

          <Flex justifyContent="flex-start">
            <Button disabled={isDisabled} icon="plus" onClick={handleAddLineClick} variant="text">
              <Text variant="bodySM" weight="bold">
                {t('imported_invoices.review.form.add_line')}
              </Text>
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  )
}
